import styled from "@emotion/styled";
import {
  COLOR_APP_TEXT,
  COLOR_GREEN_BORDER,
  COLOR_GREEN_CHECKBOX_HOVER,
  COLOR_LIGHT_BLUE_CHECKBOX_HOVER,
  MOBILE_SCREEN,
  COLOR_TERTIARY,
  COLOR_INPUT_DEFAULT_BORDER,
  COLOR_INPUT_BORDER_HOVER,
  BORDER_RADIUS_DEFAULT,
} from "../theme";
import { Paragraph } from "../typography/Paragraph";

const StyledRadioContainer = styled.div<{ isChecked: boolean }>`
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: start; // Align to top, then manually position radio to first line center
  padding: 24px;
  gap: 8px;
  margin: 4px 0;
  user-select: none;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  border: ${({ isChecked }) =>
    isChecked
      ? `1px solid ${COLOR_GREEN_BORDER}`
      : `1px solid ${COLOR_INPUT_DEFAULT_BORDER}`};
  background-color: ${({ isChecked }) =>
    isChecked ? COLOR_GREEN_CHECKBOX_HOVER : "transparent"};
  &:hover {
    background-color: ${({ isChecked }) =>
      isChecked ? COLOR_GREEN_CHECKBOX_HOVER : COLOR_LIGHT_BLUE_CHECKBOX_HOVER};
    input {
      border: 1px solid ${COLOR_APP_TEXT};
    }
    border: ${({ isChecked }) =>
      isChecked
        ? `1px solid ${COLOR_GREEN_BORDER}`
        : `1px solid ${COLOR_INPUT_BORDER_HOVER}`};
    cursor: pointer;
  }
  ${MOBILE_SCREEN} {
    padding: 8px;
  }
`;

const StyledRadio = styled.input`
  /* Remove default radio appearance */
  -webkit-appearance: none;
  appearance: none;

  /* Visual styling for the un-checked state */
  width: 24px;
  height: 24px;
  border: 1px solid ${COLOR_INPUT_DEFAULT_BORDER}; /* outer circle border color */
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  position: relative; /* so we can absolutely position the tick */
  margin-top: 0px; // Fine-tune alignment with text baseline in first line

  &:hover {
    border-color: ${COLOR_INPUT_BORDER_HOVER}; /* Outer circle border */
  }

  &:checked {
    background-color: ${COLOR_APP_TEXT};
    border-color: ${COLOR_APP_TEXT};
  }

  /* Show a check ('tick') when checked */
  &:checked::after {
    content: "";
    position: absolute;
    top: 5px;
    left: 8px;
    width: 4px;
    height: 8px;
    border: 1px solid ${COLOR_TERTIARY}; /* color of the check */
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

export interface RadioProps {
  label: string;
  isChecked: boolean;
  id: string;
  onToggle: () => void;
}

const Radio: React.FC<RadioProps> = ({
  isChecked,
  label,
  id,
  onToggle,
  ...props
}: RadioProps) => {
  return (
    <StyledRadioContainer isChecked={isChecked} onClick={onToggle} {...props}>
      <StyledRadio
        type="radio"
        onChange={() => null}
        id={id}
        checked={isChecked}
        aria-label={label}
        data-testid="radio"
      />
      <label
        // This is to prevent the default behavior of triggering the onchange
        // for the radio while maintaining an accessible label
        onClick={(e) => {
          e.preventDefault();
        }}
        htmlFor={id}
      >
        <Paragraph fontWeight={100} color={COLOR_APP_TEXT}>
          <span dangerouslySetInnerHTML={{ __html: label }} />
        </Paragraph>
      </label>
    </StyledRadioContainer>
  );
};

export default Radio;
